// src/pages/ParticipantScreen.js
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ADLPopup from '../components/ADLPopup';
import SensorDataBox from '../components/SensorDataBox';
import TokenGrid from '../components/TokenGrid';
import { getHomeForUser, getUser, saveAnnotation } from '../lib/assets';

function formatDate(date) {
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function ParticipantScreen({ data: initialData, setData, sensors, isCaregiverScreen, hasCaregiver, targetDate }) {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState('Morning');
  const [localData, setLocalData] = useState([]);
  const [selectedTokenIdx, setSelectedTokenIdx] = useState(null); // null = sidebar hidden

  useEffect(() => {
    const start = dayjs(targetDate).startOf('day');
    const slots = Array.from({ length: 96 }, (_, i) => start.add(i * 15, 'minute'));
    const newData = slots.map((slot, idx) => {
      const existing = initialData.find(item => dayjs(item.timestamp).isSame(slot, 'minute'));
      return existing ? { ...existing, adls: Array.isArray(existing.adls) ? existing.adls : [], reporter: existing.reporter || '', timestamp: slot.toISOString() }
        : { timestamp: slot.toISOString(), adls: [], reporter: '', kitchen: { temperature: 0, light: 0, motion: 0 }, livingRoom: { temperature: 0, light: 0, motion: 0 }, bedroom: { temperature: 0, light: 0, motion: 0 }, bathroom: { temperature: 0, light: 0, motion: 0 } };
    });
    setLocalData(newData);
  }, [initialData, targetDate]);

  const getTokensForPeriod = () => {
    const startIdx = selectedPeriod === 'Morning' ? 0 : selectedPeriod === 'Evening' ? 28 : 56;
    const count = selectedPeriod === 'Night' ? 40 : 28;
    return localData.slice(startIdx, startIdx + count).map((slot, idx) => {
      const t = dayjs(slot.timestamp);
      return { label: t.format('h:mm') + ' - ' + t.add(15, 'minute').format('h:mm'), timestamp: slot.timestamp, originalIndex: startIdx + idx, adls: slot.adls || [] };
    });
  };
  const tokenData = getTokensForPeriod();

  const uploadData = async (activities) => {
    try {
      for (const act of activities.filter(a => a.adls.length > 0)) {
        for (const adl of act.adls) {
          const annotation = {
            house_id: (await getHomeForUser(await getUser())).id,
            category_id: 'event_category:annotation',
            start_at_ms: dayjs(act.timestamp).utc().valueOf(),
            end_at_ms: dayjs(act.timestamp).add(15, 'minutes').utc().valueOf(),
            expected: adl,
            observed: act.reporter,
            tz_offset_sec: dayjs().utcOffset() * 60,
          };
          await saveAnnotation(annotation);
        }
      }
    } catch (e) { console.error(e); }
  };

  const onDone = () => {
    if (isCaregiverScreen || !hasCaregiver) {
      uploadData(localData);
      setData(localData);
      navigate('/done');
    } else {
      navigate('/caregiver');
    }
  };

  const handleTokenClick = (index) => {
    setSelectedTokenIdx(index);
  };

  const handleAdlSelect = (adl) => {
    if (selectedTokenIdx === null) return;
    const token = tokenData[selectedTokenIdx];
    const slotIdx = token.originalIndex;
    const prefix = isCaregiverScreen ? 'C_' : 'P_';
    const prefixed = `${prefix}${adl}`;

    setLocalData(prev => {
      const copy = [...prev];
      const slot = copy[slotIdx];
      if (!Array.isArray(slot.adls)) slot.adls = [];
      if (!slot.adls.includes(prefixed)) slot.adls.push(prefixed);
      slot.reporter = isCaregiverScreen ? 'caregiver' : 'participant';
      return copy;
    });
  };

  const handleSidebarClose = () => {
    setSelectedTokenIdx(null);
  };

  return (
    <div className="screen active" style={{ position: 'relative' }}>
      <h2>Sensor Data for {formatDate(targetDate)}</h2>
      <label>Time Period: 
      <select className="dropdown" value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}>
        <option value="Morning">Morning</option>
        <option value="Evening">Evening</option>
        <option value="Night">Night</option>
      </select></label>
      <div className="page-indicator">{isCaregiverScreen ? 'Caregiver Page' : 'Client Page'}</div>

      <SensorDataBox
        sensors={sensors}
        tokenData={selectedTokenIdx !== null ? localData[tokenData[selectedTokenIdx].originalIndex] : null}
        detectionMessages={{}}
      />

      <div className="token-grid-container">
        <TokenGrid data={tokenData} onTokenClick={handleTokenClick} isCaregiverScreen={isCaregiverScreen} />
      </div>

      {/* Right Sidebar - ADL List */}
      {selectedTokenIdx !== null && (
        <ADLPopup
          token={tokenData[selectedTokenIdx]}
          onSelectAdl={handleAdlSelect}
          onClose={handleSidebarClose}
        />
      )}

      <button className="done-button" onClick={onDone} style={{ marginTop: '20px' }}>
        {isCaregiverScreen ? 'All Done (Caregiver)' : 'Done (Client)'}
      </button>
    </div>
  );
}

export default ParticipantScreen;